import { z } from "zod";
import { bedtimeLayoutTemplateIdSchema, bedtimeLayoutTemplateOptionsSchema, } from "./bedtime-book-design.js";
import { bedtimePageGrammarIdSchema, bedtimeReadingTurnKindSchema } from "./bedtime-publication.js";
// Every template slot names its layout; the grammar's unnamed base layout is never a template's.
const variantIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .refine((value) => value !== "BASE", "Template slots name a governed layout variant.");
/**
 * Owner decision 2026-10-09: a buyer picks one of five original book templates. A template is a
 * pre-validated rhythm over the existing page-grammar catalog — one grammar and layout variant for
 * each of the 12 story spreads — so text, picture geometry and provider canvases are known before
 * any paid step. Templates are original designs informed by aggregate measurements of a private
 * reference corpus; no template reproduces any book's page sequence.
 */
export const bedtimeLayoutTemplateSlotSchema = z
    .object({
    turnIndex: z.number().int().min(1).max(12),
    turnKind: bedtimeReadingTurnKindSchema,
    grammarId: bedtimePageGrammarIdSchema,
    /** The layout variant used under each text emphasis; "BASE" is the grammar's own slots. */
    variants: z
        .object({
        BALANCED: variantIdSchema,
        MOSTLY_TOP: variantIdSchema,
        MOSTLY_BOTTOM: variantIdSchema,
    })
        .strict(),
    shotScale: z.enum(["WIDE", "MEDIUM", "CLOSE"]),
    editorialRole: z.string().trim().min(1).max(200),
    /**
     * A wordless slot that carries the beat before it on (owner decision 2026-10-10: fewer
     * wordless spreads): when that beat's words split into two parts that both fit, the later
     * part turns the page onto this text page. Otherwise the slot stays the wordless spread above.
     */
    continuation: z
        .object({
        grammarId: bedtimePageGrammarIdSchema,
        variants: z
            .object({
            BALANCED: variantIdSchema,
            MOSTLY_TOP: variantIdSchema,
            MOSTLY_BOTTOM: variantIdSchema,
        })
            .strict(),
        shotScale: z.enum(["WIDE", "MEDIUM", "CLOSE"]),
        editorialRole: z.string().trim().min(1).max(200),
    })
        .strict()
        .optional(),
})
    .strict();
export const bedtimeLayoutTemplateV1Schema = z
    .object({
    contract: z.literal("BEDTIME_LAYOUT_TEMPLATE_V1"),
    templateId: bedtimeLayoutTemplateIdSchema,
    version: z.string().regex(/^\d+\.\d+\.\d+$/u),
    name: z.string().trim().min(1).max(60),
    summary: z.string().trim().min(1).max(300),
    bestFor: z.string().trim().min(1).max(200),
    profileId: z.literal("bedtime-story-hardcover-8x11-landscape-32"),
    slots: z.array(bedtimeLayoutTemplateSlotSchema).length(12),
    defaultOptions: bedtimeLayoutTemplateOptionsSchema,
})
    .strict()
    .superRefine((template, context) => {
    template.slots.forEach((slot, index) => {
        if (slot.turnIndex !== index + 1)
            context.addIssue({
                code: "custom",
                path: ["slots", index, "turnIndex"],
                message: "Template slots must cover story turns 1 to 12 in order.",
            });
        if ((slot.turnKind === "WORDLESS_BREATH") !== (slot.grammarId === "WORDLESS_SPREAD"))
            context.addIssue({
                code: "custom",
                path: ["slots", index, "turnKind"],
                message: "Wordless spreads, and only wordless spreads, hold wordless turns.",
            });
        // Eight storyboard beats become twelve turns, with a wordless breath after beats 2, 4, 6
        // and 8, so turns 3, 6, 9 and 12 are wordless.
        if ((slot.turnKind === "WORDLESS_BREATH") !== (slot.turnIndex % 3 === 0))
            context.addIssue({
                code: "custom",
                path: ["slots", index, "turnKind"],
                message: "Templates follow the story rhythm: turns 3, 6, 9 and 12 are wordless.",
            });
    });
    template.slots.forEach((slot, index) => {
        if (slot.continuation &&
            (slot.turnKind !== "WORDLESS_BREATH" ||
                ["WORDLESS_SPREAD", "EXTERNAL_RAIL_DIGITAL"].includes(slot.continuation.grammarId)))
            context.addIssue({
                code: "custom",
                path: ["slots", index, "continuation"],
                message: "Only a wordless slot carries a beat on, and it does so on a printed text page.",
            });
    });
    if (template.slots.some(({ grammarId }) => grammarId === "EXTERNAL_RAIL_DIGITAL"))
        context.addIssue({
            code: "custom",
            path: ["slots"],
            message: "A printed template cannot use the digital-only rail grammar.",
        });
});
//# sourceMappingURL=bedtime-layout-templates.js.map